import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/layout/PageHero";
import { api } from "@/lib/api";
import { useDevAuth } from "@/context/DevAuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

// Without ?token: "Mot de passe oublié" request form. With ?token: choose a new password.
export default function DevPassword() {
  const { t } = useI18n();
  const { enter } = useDevAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [value, setValue] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (token) {
        await enter("/dev/auth/reset-password", { token, password: value });
        navigate("/espace-developpeur", { replace: true });
      } else {
        await api.post("/dev/auth/forgot-password", { email: value });
        setSent(true);
      }
    } catch (err) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={token ? "dev-reset-page" : "dev-forgot-page"}>
      <PageHero eyebrow={t("devAuth.eyebrow")} title={t(token ? "pwd.resetTitle" : "pwd.forgotTitle")} />
      <section className="mx-auto max-w-md px-4 sm:px-0">
        <form onSubmit={submit} className="card-carbon fade-up space-y-5 rounded-3xl p-6 sm:p-8" data-testid="dev-password-form">
          {sent ? (
            <p className="flex items-start gap-2 text-sm text-emerald-200" data-testid="dev-forgot-sent"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {t("pwd.sent")}</p>
          ) : (
            <>
              <p className="text-sm text-neutral-400">{t(token ? "pwd.resetHint" : "pwd.forgotHint")}</p>
              {error && <p className="flex items-center gap-2 rounded-xl border border-red-500/50 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="dev-password-error"><AlertTriangle className="h-4 w-4 shrink-0" /> {error}</p>}
              <div className="space-y-2">
                <Label htmlFor="pwd-value">{t(token ? "pwd.newPassword" : "login.email")}</Label>
                <Input id="pwd-value" required type={token ? "password" : "email"} minLength={token ? 8 : undefined} value={value}
                  onChange={(e) => setValue(e.target.value)} className="h-12 bg-black/50" data-testid="dev-password-input-field" />
              </div>
              <button type="submit" className="btn-red w-full" disabled={loading} data-testid="dev-password-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : token ? <KeyRound className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                {t(token ? "pwd.resetSubmit" : "pwd.forgotSubmit")}
              </button>
            </>
          )}
          <Link to="/espace-developpeur/connexion" className="block text-center text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="dev-password-back-link">{t("pwd.back")}</Link>
        </form>
      </section>
    </div>
  );
}
